/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, getter-return, no-restricted-properties */
// Standard Library imports
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import EmberObject, { get, action, computed } from '@ember/object';
import { setDiff, mapBy, intersect, alias } from '@ember/object/computed';
import { cancel, debounce, next } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import config from 'linced-app/config/environment';
// Internal imports
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
// External imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Atsi from '@linc-technologies/linced-common/enums/au-atsi';
import moment from 'moment';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import { matchYearLevel } from 'linced-app/utils/year-levels';
import { observes } from '@ember-decorators/object';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';

export default class AnalysisController extends Controller {
  @service
  router;

  @action
  setAbsentPercentageFilter(value) {
    this.absentPercentageFilter = value;
  }

  @action
  setLatePercentageFilter(value) {
    this.latePercentageFilter = value;
  }

  @action
  setMinorPartialAbsencesFilter(value) {
    this.minorPartialAbsencesFilter = value;
  }

  @action
  setPartialAbsencesFilter(value) {
    this.partialAbsencesFilter = value;
  }

  @action
  setPresentACalcFilter(value) {
    this.presentACalcFilter = value;
  }

  @action
  setPresentPCalcFilter(value) {
    this.presentPCalcFilter = value;
  }

  @action
  setPresentPercentageFilter(value) {
    this.presentPercentageFilter = value;
  }

  @action
  setTruancyFilterJ(value) {
    this.truancyFilterJ = value;
  }

  @action
  setTruancyFilterO(value) {
    this.truancyFilterO = value;
  }

  @action
  setTruancyFilterP(value) {
    this.truancyFilterP = value;
  }

  @action
  setTruancyFilterU(value) {
    this.truancyFilterU = value;
  }

  Jurisdiction = Jurisdiction;
  matchYearLevel = matchYearLevel;
  queryParams = ['dateFrom', 'dateTo', 'termId', 'year'];

  // Services
  @service
  attendanceCodes;

  @service
  attendance;

  @service
  session;

  @service
  dates;

  @service
  fetch;

  @service
  strings;

  @service
  toaster;

  @alias('session.school')
  school;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get isNZSchool() {
    return this.jurisdiction === Jurisdiction.NZ_MOE;
  }

  @computed('jurisdiction')
  get isNSWSchool() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @computed('jurisdiction')
  get showLateCount() {
    return this.jurisdiction !== Jurisdiction.AU_NSW;
  }

  @computed('dates.previousAcademicYearLabel')
  get previousAcademicDates() {
    let previousYear = this.dates.previousAcademicYearLabel;
    let academicYearDates =
      this.dates.halfyearDatesForAcademicYear(previousYear);
    return {
      startDate: academicYearDates.startDate.format('YYYY-MM-DD'),
      endDate: academicYearDates.endDate.format('YYYY-MM-DD'),
    };
  }

  @computed('year')
  get isLastYear() {
    return this.year && parseInt(this.year, 10) !== moment().year();
  }

  @computed('dateFrom', 'dateTo', 'previousAcademicDates', 'isLastYear')
  get datesValid() {
    if (this.isLastYear) {
      return (
        moment(this.dateFrom).isSameOrAfter(
          this.previousAcademicDates.startDate,
        ) &&
        moment(this.dateTo).isSameOrBefore(this.previousAcademicDates.endDate)
      );
    }
    return true;
  }

  // _datesChanged updates the "new" date properties to keep them in line with the query params.
  @observes('dateFrom', 'dateTo')
  _datesChanged() {
    this.newDateFrom = this.dateFrom;
    this.newDateTo = this.dateTo;
  }

  // updateDates should be run debounced in order to prevent the route's model reloading
  // as soon as a "valid" date is entered, as the first digit of the year will make it "valid".
  updateDates() {
    let props = {
      termId: '',
    };
    let hasNewValue = false;

    if (this.newDateFrom !== this.dateFrom) {
      props.dateFrom = this.newDateFrom;
      props.newDateFrom = null;
      hasNewValue = true;
    }

    if (this.newDateTo !== this.dateTo) {
      props.dateTo = this.newDateTo;
      props.newDateTo = null;
      hasNewValue = true;
    }

    if (hasNewValue) {
      this.setProperties(props);
    }
  }

  @computed('termId', 'model.terms.[]')
  get selectedTerm() {
    return this.model.terms.find((item) => item.id === this.termId);
  }

  // Filters located outside the table
  _pendingDateUpdate = null;
  @tracked dateFrom = null;

  @tracked newDateFrom = null;
  @tracked dateTo = null;
  @tracked
  newDateTo = null;
  @tracked termId = null;
  @tracked year = null;
  @tracked
  groupFilter = [];
  @tracked
  genderFilter = [];
  @tracked
  codeFilter = [];
  @tracked
  ethnicityFilter = [];

  // groupId / group are not currently used by the UI, but allow for single-group filtering.
  @tracked groupId = null;

  get group() {
    if (isEmpty(this.groupId)) {
      return;
    }
    return this.model.groups.find((group) => group.id === this.groupId);
  }

  @computed('model.groups.[]')
  get sortedGroups() {
    return this.model.groups
      .slice()
      .sort(
        (a, b) => a.type - b.type || (a.name || '').localeCompare(b.name || ''),
      );
  }

  @computed('sortedGroups.[]')
  get filteredGroups() {
    return this.sortedGroups
      .filter((group) => {
        if (group.type === GroupType.GROUP) {
          // Omit My Groups which the current user isn't a current staff member in.
          let staff = group.get('currentStaff') ?? [];
          return !!staff.find(
            (item) => item.personId === this.session.personId,
          );
        }
        if (group.status === NSWStatus.INACTIVE) {
          return false;
        }
        // Include all other groups.
        return true;
      })
      .sort(byKeys('type', 'label'));
  }

  @setDiff('filteredGroups', 'groupFilter')
  remainingGroups;

  @tracked
  yearLevelFilter = [];

  @setDiff('school.yearLevels', 'yearLevelFilter')
  remainingYearLevels;

  ethnicities = [
    'Māori',
    'Tokelauan',
    'Fijian',
    'Niuean',
    'Tongan',
    'Cook Islands Māori',
    'Samoan',
    'Other Pacific',
    'Southeast Asian',
    'Indian',
    'Chinese',
    'Other Asian',
    'Middle Eastern',
    'Latin American',
    'African',
    'Other',
    'Other European',
    'New Zealand',
    'Not Stated',
  ];

  @computed('isNZSchool', 'isNSWSchool')
  get jurisdictionalEthnicFilters() {
    if (this.isNZSchool) {
      return this.ethnicities;
    } else if (this.isNSWSchool) {
      return Atsi.values.map((atsi) => atsi.label);
    } else {
      // Default to NZMoE
      return this.ethnicities;
    }
  }

  @computed('isNZSchool', 'isNSWSchool')
  get ethnicityLabel() {
    if (this.isNZSchool) {
      return 'Ethnicity';
    } else if (this.isNSWSchool) {
      return 'ATSI';
    } else {
      return 'Ethnicity';
    }
  }

  @setDiff('jurisdictionalEthnicFilters', 'ethnicityFilter')
  remainingEthnicities;

  @computed('model.genderField.options.[]')
  get genders() {
    let options = this.get('model.genderField.options');
    if (!isEmpty(options)) {
      // Use options directly from the gender field.
      return options;
    }
    return [
      { value: 'M', label: 'Male' },
      { value: 'F', label: 'Female' },
    ];
  }

  @setDiff('genders', 'genderFilter')
  remainingGenders;

  @computed('attendanceCodes.allCodes.@each.old', 'newDateFrom')
  get codes() {
    if (moment(this.newDateFrom).year() >= 2025 || isEmpty(this.newDateFrom)) {
      return this.attendanceCodes.allCodes.filter((item) => !item.old);
    }
    return this.attendanceCodes.allCodes;
  }

  @setDiff('codes', 'codeFilter')
  remainingCodes;

  matchCode(option, code) {
    return (
      option.code === code.toUpperCase() ||
      option.label.toLowerCase().indexOf(code.toLowerCase())
    );
  }

  @mapBy('codeFilter', 'code')
  codeFilterCodes;

  @computed('codes.[]', 'codeFilter.[]')
  get codeColumns() {
    if (!isEmpty(this.codeFilter)) {
      return this.codeFilter;
    }
    return this.codes;
  }

  @computed('codeColumns.@each.code')
  get codeColumnKeys() {
    return (this.codeColumns || []).map((code) => code.code);
  }

  dayCodes = Object.freeze(['P', 'U', 'J']);

  // In-table field filters
  @tracked presentPercentageFilter = '';

  @tracked absentPercentageFilter = '';
  @tracked latePercentageFilter = '';
  @tracked halfDayFilterP = '';
  @tracked halfDayFilterU = '';
  @tracked halfDayFilterJ = '';
  @tracked partialAbsencesFilter = '';
  @tracked minorPartialAbsencesFilter = '';
  @tracked truancyFilterP = '';
  @tracked truancyFilterJ = '';
  @tracked truancyFilterU = '';
  @tracked truancyFilterO = '';
  @tracked presentACalcFilter = '';
  @tracked presentPCalcFilter = '';

  // attendanceFieldFilters combines all in-table attendance filters into an array
  // if the values are non-empty
  @computed(
    'presentPercentageFilter',
    'absentPercentageFilter',
    'latePercentageFilter',
    'halfDayFilterP',
    'halfDayFilterU',
    'halfDayFilterJ',
    'partialAbsencesFilter',
    'minorPartialAbsencesFilter',
    'truancyFilterP',
    'truancyFilterJ',
    'truancyFilterU',
    'truancyFilterO',
    'presentACalcFilter',
    'presentPCalcFilter',
    'isNZSchool',
  )
  get attendanceFieldFilters() {
    let filters = [
      // Keys
      'presentPercentage',
      'absentPercentage',
      'latePercentage',
    ].reduce((acc, key) => {
      let value = this.get(`${key}Filter`);
      if (!isEmpty(value)) {
        acc.push({ key, value });
      }
      return acc;
    }, []);

    if (!this.isNZSchool) {
      [
        { key: 'halfDayCounts.P', value: this.halfDayFilterP },
        { key: 'halfDayCounts.U', value: this.halfDayFilterU },
        { key: 'halfDayCounts.J', value: this.halfDayFilterJ },
        { key: 'partialAbsencesValue', value: this.partialAbsencesFilter },
        {
          key: 'minorPartialAbsencesValue',
          value: this.minorPartialAbsencesFilter,
        },
      ].forEach((filter) => {
        if (!isEmpty(filter.value)) {
          filters.push(filter);
        }
      });
    } else {
      [
        { key: 'truancyP', value: this.truancyFilterP },
        { key: 'truancyJ', value: this.truancyFilterJ },
        { key: 'truancyU', value: this.truancyFilterU },
        { key: 'truancyO', value: this.truancyFilterO },
        { key: 'presentA', value: this.presentACalcFilter },
        { key: 'presentP', value: this.presentPCalcFilter },
      ].forEach((filter) => {
        if (!isEmpty(filter.value)) {
          filters.push(filter);
        }
      });
    }

    return filters;
  }

  // Table data

  @computed(
    'strings.timetablePeriods',
    'selectedTerm',
    'year',
    'group.name',
    'isNZSchool',
    'dateFrom',
    'dateTo',
    'model.terms.[]',
  )
  get title() {
    let todaysDate = moment().format('DD/MM/YYYY');
    let firstTermOfYear = this.model.terms[0];
    let year = this.year || moment().year();
    let formattedDateTo = moment(this.dateTo).format('DD/MM/YYYY');
    let formattedDateFrom = moment(this.dateFrom).format('DD/MM/YYYY');
    if (!isEmpty(this.dateTo)) {
      year = moment(this.dateTo, 'YYYY-MM-DD').year();
    }

    let title = 'Attendance Analysis';
    if (!this.isNZSchool) {
      title = 'Attendance Analysis (calculated day code)';
    }
    if (!isEmpty(this.group)) {
      title = `${title} for ${this.group.name}`;
    }
    if (isEmpty(this.selectedTerm)) {
      if (
        isEmpty(this.dateFrom) ||
        isEmpty(firstTermOfYear) ||
        (this.dateFrom === firstTermOfYear.start &&
          moment(this.dateTo).isSameOrAfter(moment()))
      ) {
        title = `${year} YTD ${title}`;
      }
    } else {
      title = `${title} - Term ${this.selectedTerm.term}`;
    }
    if (!isEmpty(this.dateFrom)) {
      title = `${title} - ${formattedDateFrom}`;
      if (isEmpty(this.dateTo)) {
        title = `${title} to ${todaysDate}`;
      }
    }
    if (!isEmpty(this.dateTo)) {
      title = `${title} to ${formattedDateTo}`;
    }

    return title;
  }

  @computed(
    'model.analyses.[]',
    'groupFilter.[]',
    'yearLevelFilter.[]',
    'ethnicityFilter.[]',
    'genderFilter.[]',
  )
  get analyses() {
    if (
      isEmpty(this.groupFilter) &&
      isEmpty(this.yearLevelFilter) &&
      isEmpty(this.ethnicityFilter) &&
      isEmpty(this.genderFilter)
    ) {
      return this.model.analyses;
    }

    let hasGroups = !isEmpty(this.groupFilter);
    let hasYearLevels = !isEmpty(this.yearLevelFilter);
    let hasEthnicities = !isEmpty(this.ethnicityFilter);
    let hasGenders = !isEmpty(this.genderFilter);
    let genderFilter = this.genderFilter.map((item) => item.value);

    return this.model.analyses.filter((analysis) => {
      let include = true;
      if (hasGroups) {
        include = this.groupFilter.some((group) => {
          return group.currentLearners.find(
            (item) => item.personId === analysis.person_id,
          );
        });
      }
      if (include && hasYearLevels) {
        // Stringify learner's current year level, as the filter array contains strings.
        include = this.yearLevelFilter.includes(
          '' + analysis.get('person.currentYearLevel'),
        );
      }
      if (include && hasEthnicities) {
        if (this.jurisdiction === Jurisdiction.AU_NSW) {
          include = this.ethnicityFilter.includes(analysis.get('person.atsi'));
        } else {
          include = this.ethnicityFilter.includes(
            analysis.get('person.displayEthnicity'),
          );
        }
      }
      if (include && hasGenders) {
        include = genderFilter.includes(analysis.get('person.gender'));
      }
      return include;
    });
  }

  @computed('model.movementCodes.@each.{personId,counts}')
  get movementCountsByPerson() {
    let map = {};
    let movementCodes = this.model.movementCodes;
    if (isEmpty(movementCodes)) {
      return map;
    }
    movementCodes.forEach((movement) => {
      let personId = movement.get('personId');
      if (personId) {
        map[personId] = movement.get('counts') || {};
      }
    });
    return map;
  }

  @computed('model.dayCodeAnalyses.[]')
  get dayCodeAnalysesByPerson() {
    let map = {};
    let analyses = this.model.dayCodeAnalyses;
    if (isEmpty(analyses)) {
      return map;
    }
    analyses.forEach((analysis) => {
      let personId = analysis.get('person_id');
      if (personId) {
        map[personId] = analysis;
      }
    });
    return map;
  }

  @tracked
  dayCodesByPerson = null;
  @tracked
  recordsByPerson = null;
  @tracked attendanceRecordsLoaded = false;

  loadData() {
    let params = `dateFrom=${this.dateFrom}&dateTo=${this.dateTo}`;
    let recordsUrl = `${config.APP.apiHost}/attendance/v4/recordsbylearners?${params}`;
    let dayCodesUrl = `${config.APP.apiHost}/attendance/v4/nzmoedaysbylearners?${params}`;

    return this.fetch
      .request(recordsUrl)
      .then((recordsResp) => {
        this.recordsByPerson = recordsResp.recordsByLearner;
        return this.fetch.request(dayCodesUrl).then((dayCodesResp) => {
          this.dayCodesByPerson = dayCodesResp.nzMoeDaysByLearner;
          let halfDayCodesMap = {};
          this.model.dayCodeAnalyses.forEach((analyses) => {
            let personId = analyses.get('person.id');
            halfDayCodesMap[personId] = this.getFilteredAnalysis(
              this.dayCodesByPerson.find((item) => item.personId === personId)
                .nzMoeDays || [],
              this.recordsByPerson.find((item) => item.personId === personId)
                .records,
              this.codeFilterCodes,
            );
          });
          this.attendanceRecordsLoaded = true;
          return halfDayCodesMap;
        });
      })
      .catch(() => {
        this.toaster.error('Failed to load attendance records');
      });
  }

  @computed(
    'model.dayCodeAnalyses.[]',
    'codeFilterCodes.[]',
    'codeFilter.[]',
    'attendanceRecordsLoaded',
  )
  get halfDayCodesMap() {
    if (isEmpty(this.codeFilter)) {
      return this.model.dayCodeAnalyses;
    }
    if (!this.attendanceRecordsLoaded) {
      return this.loadData();
    } else {
      let halfDayCodesMap = {};
      this.model.dayCodeAnalyses.forEach((analyses) => {
        let personId = analyses.get('person.id');
        halfDayCodesMap[personId] = this.getFilteredAnalysis(
          this.dayCodesByPerson.find((item) => item.personId === personId)
            .nzMoeDays || [],
          this.recordsByPerson.find((item) => item.personId === personId)
            .records,
          this.codeFilterCodes,
        );
      });
      return halfDayCodesMap;
    }
  }

  getFilteredAnalysis(dayCodes, records, filters) {
    let analysis = this.attendance.calculateHalfDayAnalysis(
      dayCodes,
      records,
      filters,
    );
    let total = analysis.absent + analysis.present;
    let presentPercentage =
      total > 0 ? ((analysis.present / total) * 100).toFixed(1) : '0.0';
    let absentPercentage =
      total > 0 ? ((analysis.absent / total) * 100).toFixed(1) : '0.0';
    let lateCount = dayCodes.reduce((count, dayCode) => {
      return count + (dayCode.isLate ? 1 : 0);
    }, 0);

    return Object.assign({}, analysis, {
      lateCount,
      totalWholeDays: total / 2,
      presentPercentage,
      absentPercentage,
    });
  }

  // tableData contains computed array of analyses
  // based on whether the codes and/or attendance fields filters have been applied
  @computed(
    'analyses.[]',
    'codeFilter.[]',
    'attendanceFieldFilters.[]',
    'isNZSchool',
    'movementCountsByPerson',
    'dayCodeAnalysesByPerson',
    'halfDayCodesMap',
    'attendanceRecordsLoaded',
  )
  get tableData() {
    let rows;
    if (isEmpty(this.codeFilter)) {
      rows = this.analyses || [];
    } else {
      // We've applied the code filter, and need to re-calculate certain values accordingly
      let codes = this.codeFilterCodes;
      let filteredCodes = {
        truancyP: this.attendanceCodes.truancyPCodes.filter((code) =>
          codes.includes(code.code),
        ),
        truancyJ: this.attendanceCodes.truancyJCodes.filter((code) =>
          codes.includes(code.code),
        ),
        truancyU: this.attendanceCodes.truancyUCodes.filter((code) =>
          codes.includes(code.code),
        ),
        truancyO: this.attendanceCodes.truancyOCodes.filter((code) =>
          codes.includes(code.code),
        ),
        presentP: this.attendanceCodes.presentPCodes.filter((code) =>
          codes.includes(code.code),
        ),
        presentA: this.attendanceCodes.presentACodes.filter((code) =>
          codes.includes(code.code),
        ),
      };
      filteredCodes.hasPresentCodes = !isEmpty(filteredCodes.presentP);
      filteredCodes.hasAbsentCodes = !isEmpty(filteredCodes.presentA);
      rows = (this.analyses || []).reduce((analyses, analysis) => {
        analysis = this.applyCodeFilter(analysis, filteredCodes);
        let rowHasData =
          analysis.truancyP ||
          analysis.truancyJ ||
          analysis.truancyU ||
          analysis.truancyO ||
          analysis.presentP;
        if (!rowHasData) {
          // Row has no data after applying the code filters.
          return analyses;
        }
        analyses.push(analysis);
        return analyses;
      }, []);
    }

    rows = rows || [];
    if (rows.toArray) {
      rows = rows.toArray();
    }
    rows = rows.map((analysis) => this.decorateAnalysisForDisplay(analysis));
    if (!isEmpty(this.attendanceFieldFilters)) {
      rows = rows.filter((analysis) => this.matchesRateFilters(analysis));
    }
    return rows;
  }

  decorateAnalysisForDisplay(analysis) {
    if (!analysis) {
      return analysis;
    }
    analysis.set('counts', this.normalizeCodeValues(analysis.get('counts')));
    if (this.isNZSchool) {
      return analysis;
    }
    let personId = analysis.get('person.id');
    if (isEmpty(personId) && analysis.person) {
      personId = analysis.person.get
        ? analysis.person.get('id')
        : analysis.person.id;
    }
    let movementCounts = this.normalizeMovementCounts(
      this.getMovementCountsForPerson(personId),
    );
    analysis.set('movementCounts', movementCounts);
    let dayCodeAnalysis = this.getNonNzDayCodeAnalysis(personId);
    analysis.set('halfDayAnalysis', dayCodeAnalysis || null);

    let normalizedHalfDayAnalysis = this.normalizeHalfDayAnalysis(
      this.dayCodeAnalysesByPerson[personId],
    );

    let baseHalfDayAnalysis = isEmpty(this.codeFilter)
      ? normalizedHalfDayAnalysis
      : dayCodeAnalysis;

    let halfDayCounts = this.buildHalfDayCounts(dayCodeAnalysis);
    analysis.set('halfDayCounts', EmberObject.create(halfDayCounts));

    analysis.set(
      'partialAbsencesValue',
      get(baseHalfDayAnalysis || {}, 'partialAbsences') ?? 0,
    );

    analysis.set(
      'minorPartialAbsencesValue',
      get(baseHalfDayAnalysis || {}, 'minorPartialAbsences') ?? 0,
    );

    if (dayCodeAnalysis) {
      analysis.set('presentPercentage', dayCodeAnalysis.presentPercentage);
      analysis.set('absentPercentage', dayCodeAnalysis.absentPercentage);
    } else if (!isEmpty(this.codeFilter)) {
      analysis.set('presentPercentage', null);
      analysis.set('absentPercentage', null);
    }
    return analysis;
  }

  buildHalfDayCounts(halfDayAnalysis) {
    let counts = {};
    this.dayCodes.forEach((code) => {
      let value = 0;
      if (halfDayAnalysis) {
        value = (get(halfDayAnalysis.counts, code) ?? 0) / 2;
      }
      counts[code] = value;
    });
    return counts;
  }

  normalizeMovementCounts(counts = {}) {
    return this.normalizeCodeValues(counts);
  }

  normalizeCodeValues(values = {}) {
    let normalized = Object.assign({}, values);
    (this.codeColumnKeys || []).forEach((code) => {
      normalized[code] = get(normalized, code) ?? 0;
    });
    return normalized;
  }

  normalizeHalfDayAnalysis(analysis) {
    if (!analysis) {
      return null;
    }
    let getProp = (key, defaultValue = 0) => get(analysis, key) ?? defaultValue;
    let counts = getProp('counts', {});
    let normalizedCounts = {
      P: get(counts, 'P') ?? 0,
      J: get(counts, 'J') ?? 0,
      U: get(counts, 'U') ?? 0,
      I: get(counts, 'I') ?? 0,
      O: get(counts, 'O') ?? 0,
    };
    return {
      counts: normalizedCounts,
      partialAbsences: getProp('partialAbsences', 0),
      minorPartialAbsences: getProp('minorPartialAbsences', 0),
      present: getProp('present', 0),
      absent: getProp('absent', 0),
      presentPercentage: getProp('presentPercentage'),
      absentPercentage: getProp('absentPercentage'),
    };
  }

  getMovementCountsForPerson(personId) {
    if (isEmpty(personId)) {
      return {};
    }
    return this.movementCountsByPerson[personId] || {};
  }

  getNonNzDayCodeAnalysis(personId) {
    if (this.isNZSchool || isEmpty(personId)) {
      return null;
    }
    if (isEmpty(this.codeFilter)) {
      return this.normalizeHalfDayAnalysis(
        this.dayCodeAnalysesByPerson[personId],
      );
    }
    if (!this.attendanceRecordsLoaded) {
      return null;
    }
    return this.normalizeHalfDayAnalysis(this.halfDayCodesMap[personId]);
  }

  applyCodeFilter(analysis, codes) {
    let total = analysis.getCodeCount(this.codeFilter);
    let presentA = analysis.getCodeCount(codes.presentA);
    let presentP = analysis.getCodeCount(codes.presentP);
    // Re-calculate percentages for these codes only
    // Fix them to 1 decimal place to allow easy filtering
    let presentPercentage, latePercentage, absentPercentage;
    if (codes.hasPresentCodes && presentP > 0) {
      presentPercentage = ((presentP / total) * 100).toFixed(1);
    }
    if (this.codeFilterCodes.includes('L')) {
      latePercentage = analysis.latePercentage;
    }
    if (codes.hasAbsentCodes && presentA > 0) {
      absentPercentage = ((presentA / total) * 100).toFixed(1);
    }
    return EmberObject.create({
      person: analysis.person,
      counts: analysis.counts,
      total,
      truancyP: analysis.getCodeCount(codes.truancyP),
      truancyJ: analysis.getCodeCount(codes.truancyJ),
      truancyU: analysis.getCodeCount(codes.truancyU),
      truancyO: analysis.getCodeCount(codes.truancyO),
      presentP: analysis.getCodeCount(codes.presentP),
      presentA,
      presentPercentage,
      latePercentage,
      absentPercentage,
    });
  }

  matchesRateFilters(analysis) {
    return this.attendanceFieldFilters.every((filter) => {
      let dataType = DataType.FLOAT;
      let key = filter.key;
      let value = filter.value;
      // Call a filtering util
      return filterValue(analysis.get(key), dataType, value);
    });
  }

  @computed(
    'tableData.[]',
    'model.dayCodeAnalyses.[]',
    'halfDayCodesMap.[]',
    'codeFilter.[]',
  )
  get halfDayCalcTotals() {
    let totals = {
      counts: {
        P: 0,
        J: 0,
        U: 0,
        I: 0,
        O: 0,
      },
      partialAbsences: 0,
      minorPartialAbsences: 0,
      present: 0,
      absent: 0,
      presentPercentage: 0,
      absentPercentage: 0,
    };
    this.tableData.forEach((analysis) => {
      let personId = analysis.get('person.id');
      let currPersonCodes = this.normalizeHalfDayAnalysis(
        analysis.get('halfDayAnalysis'),
      );
      if (!currPersonCodes) {
        if (!isEmpty(this.codeFilter)) {
          currPersonCodes = this.normalizeHalfDayAnalysis(
            this.halfDayCodesMap[personId],
          );
        } else {
          currPersonCodes = this.normalizeHalfDayAnalysis(
            this.model.dayCodeAnalyses.find(
              (item) => item.person_id === personId,
            ),
          );
        }
      }
      if (currPersonCodes) {
        // Dividing by 2 so we as we need to display the data in whole days
        totals.counts.P += currPersonCodes.counts.P / 2;
        totals.counts.J += currPersonCodes.counts.J / 2;
        totals.counts.U += currPersonCodes.counts.U / 2;
        totals.counts.I += currPersonCodes.counts.I / 2;
        totals.counts.O += currPersonCodes.counts.O / 2;
        totals.partialAbsences += currPersonCodes.partialAbsences;
        totals.minorPartialAbsences += currPersonCodes.minorPartialAbsences;
        totals.absent += currPersonCodes.absent;
        totals.present += currPersonCodes.present;
      }
    });

    // Only calculate if we have present and/or absent records to avoid "NaN"
    if (totals.present + totals.absent > 0) {
      totals.absentPercentage = (
        (totals.absent / (totals.present + totals.absent)) *
        100
      ).toFixed(1);
      totals.presentPercentage = (
        (totals.present / (totals.present + totals.absent)) *
        100
      ).toFixed(1);
    }
    return totals;
  }

  @computed('tableData.[]', 'attendanceCodes.allCodes.[]', 'isNZSchool')
  get totals() {
    let codes;
    if (isEmpty(this.codeFilter)) {
      codes = this.attendanceCodes.allCodes.map((item) => item.code);
    } else {
      codes = this.codeFilterCodes;
    }

    let totals = {
      raw: {},
      truancy: {
        P: 0,
        J: 0,
        U: 0,
        O: 0,
      },
      present: {
        P: 0,
        A: 0,
      },
      presentPercentage: 0,
      absentPercentage: 0,
      latePercentage: 0,
      total: 0,
    };

    // Initialise all codes in totals object.
    codes.forEach((code) => {
      totals.raw[code] = 0;
    });

    // Calculate totals.
    this.tableData.forEach((analysis) => {
      if (this.isNZSchool) {
        codes.forEach((code) => {
          totals.raw[code] += analysis.get(`counts.${code}`) ?? 0;
        });
        totals.truancy.P += analysis.get('truancyP') ?? 0;
        totals.truancy.J += analysis.get('truancyJ') ?? 0;
        totals.truancy.U += analysis.get('truancyU') ?? 0;
        totals.truancy.O += analysis.get('truancyO') ?? 0;
        totals.present.P += analysis.get('presentP') ?? 0;
        totals.present.A += analysis.get('presentA') ?? 0;
        totals.total += analysis.get('total') ?? 0;
      } else if (analysis.person.get('id')) {
        let movementCounts =
          analysis.get('movementCounts') ||
          this.calculatePersonMovementCounts(analysis.person.get('id'));
        codes.forEach((code) => {
          if (!isEmpty(movementCounts[code])) {
            totals.raw[code] += movementCounts[code];
          }
        });
      }
    });

    // Calculate percentages.
    if (totals.present.P > 0) {
      totals.presentPercentage = (
        (totals.present.P / totals.total) *
        100
      ).toFixed(1);
    } else {
      totals.presentPercentage = 0;
    }
    if (totals.present.A > 0) {
      totals.absentPercentage = (
        (totals.present.A / totals.total) *
        100
      ).toFixed(1);
    } else {
      totals.absentPercentage = 0;
    }
    if (totals.raw.L > 0) {
      totals.latePercentage = ((totals.raw.L / totals.total) * 100).toFixed(1);
    } else {
      totals.latePercentage = 0;
    }

    return totals;
  }

  // selected holds the array of learners used to create a new group with
  @tracked
  selected = [];

  // visiblePeople holds the array of people present in the table
  @computed('tableData.[]')
  get visiblePeople() {
    return this.tableData.map((row) => row.person);
  }

  // filteredSelectedPeople filters out people not visible in the table
  // from the selected people
  @intersect('selected', 'visiblePeople')
  filteredSelectedPeople;

  // Functions
  // clearSelection resets the selected
  clearSelection() {
    this.selected = [];
  }

  calculatePersonMovementCounts(personId) {
    return this.getMovementCountsForPerson(personId);
  }

  resetFilters() {
    this.groupFilter = [];
    this.yearLevelFilter = [];
    this.codeFilter = [];
    this.ethnicityFilter = [];
    this.genderFilter = [];
    this.presentPercentageFilter = '';
    this.absentPercentageFilter = '';
    this.latePercentageFilter = '';
    this.halfDayFilterP = '';
    this.halfDayFilterU = '';
    this.halfDayFilterJ = '';
    this.partialAbsencesFilter = '';
    this.minorPartialAbsencesFilter = '';
    this.truancyFilterP = '';
    this.truancyFilterJ = '';
    this.truancyFilterU = '';
    this.truancyFilterO = '';
    this.presentACalcFilter = '';
    this.presentPCalcFilter = '';
  }

  reset() {
    // Drop any date update still waiting on the debounce, so it cannot commit
    // query params after the route has been left, and discard the draft it
    // would have committed. The controller is a singleton, and _datesChanged
    // only fires when dateFrom/dateTo change, so an abandoned edit would
    // otherwise still be sitting in the inputs on the next visit, showing a
    // range the report was not built from.
    cancel(this._pendingDateUpdate);
    this.newDateFrom = this.dateFrom;
    this.newDateTo = this.dateTo;
    this.resetFilters();
    this.clearSelection();
  }

  @action
  selectGroup(group) {
    this.groupFilter = [...this.groupFilter, group];
  }

  @action
  removeGroup(group) {
    this.groupFilter = this.groupFilter.filter((item) => item !== group);
  }

  @action
  selectYearLevel(year) {
    this.yearLevelFilter = [...this.yearLevelFilter, year];
  }

  @action
  removeYearLevel(year) {
    this.yearLevelFilter = this.yearLevelFilter.filter((item) => item !== year);
  }

  @action
  selectEthnicity(ethnicity) {
    this.ethnicityFilter = [...this.ethnicityFilter, ethnicity];
  }

  @action
  removeEthnicity(ethnicity) {
    this.ethnicityFilter = this.ethnicityFilter.filter(
      (item) => item !== ethnicity,
    );
  }

  @action
  selectGender(gender) {
    this.genderFilter = [...this.genderFilter, gender];
  }

  @action
  removeGender(gender) {
    this.genderFilter = this.genderFilter.filter((item) => item !== gender);
  }

  @action
  selectCode(code) {
    this.codeFilter = [...this.codeFilter, code];
  }

  @action
  removeCode(code) {
    this.codeFilter = this.codeFilter.filter((item) => item !== code);
  }

  @action
  updateHalfDayFilter(code, value) {
    this.set(`halfDayFilter${code}`, value);
  }

  @action
  setSelectedTermId(term) {
    this.setProperties({
      dateFrom: term.start,
      dateTo: term.end,
      termId: term.id,
    });
  }

  @action
  onChangeDateFrom(newDate) {
    this.newDateFrom = newDate;
    this._pendingDateUpdate = debounce(this, this.updateDates, 500);
  }

  @action
  onChangeDateTo(newDate) {
    this.newDateTo = newDate;
    this._pendingDateUpdate = debounce(this, this.updateDates, 500);
  }

  @action
  refresh() {
    // A date edited within the debounce window is still sitting in
    // newDateFrom/newDateTo, so refreshing now would refetch the previous
    // range. It would never catch up either: committing the dates later
    // changes the query params, but both are refreshModel: false, so the
    // dates on screen and the report would disagree until the next click.
    // Flush the pending update first, then let the query param change reach
    // the route before it re-reads params in its model hook.
    cancel(this._pendingDateUpdate);
    this.updateDates();
    next(this, () => {
      if (this.isDestroyed || this.isDestroying) {
        return;
      }
      this.router.refresh('admin.lists.attendance.analysis');
    });
  }

  @action
  setGroup(group) {
    if (isEmpty(group)) {
      this.groupId = null;
    } else {
      this.groupId = group.id;
    }
  }

  @action
  toggleSelected(person) {
    if (this.selected.includes(person)) {
      this.selected = this.selected.filter((item) => item !== person);
    } else {
      this.selected = [...this.selected, person];
    }
  }

  @action
  toggleAll() {
    if (this.selected.length >= this.tableData.length) {
      // All or more are already selected, so
      // clear selection.
      this.clearSelection();
    } else {
      // Not all selected; select them all.
      this.selected = this.tableData.map((item) => item.person);
    }
  }
}
